import React, {JSX} from "react";
import { useField } from "react-final-form";
import { showError } from "./formUtils";
import { ExpandMoreOutlined } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useOutlinedAutocompleteStyles } from "./css";
import {
  Autocomplete,
  useMediaQuery,
  useTheme,
  TextField,
} from "@mui/material";
import { matchSorter } from "match-sorter";

function filterOptions(
  options: {
    label?: string | undefined;
    value?: string | number | undefined;
  }[],
  {
    inputValue,
  }: {
    inputValue: any;
  }
) {
  return matchSorter(options, inputValue, {
    keys: ["label", "value"],
    keepDiacritics: false,
    threshold: matchSorter.rankings.CONTAINS,
    sorter: (rankedItems) => rankedItems,
  });
}

export interface IOutlinedAutocomplete {
  validate?: (value: any) => any;
  name: string;
  options: { value?: string | number; label?: string }[];
  disabled?: boolean;
  label?: string;
  fullWidth?: boolean;
  helperText?: string;
  required?: boolean;
  endAdornment?: JSX.Element;
  inputValue?: boolean;
  id?: string;
  onFocus?: (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onKeyDown?: (
    event: React.KeyboardEvent<HTMLInputElement> & { target: HTMLInputElement }
  ) => void;
  loading?: boolean;
  ListboxComponent?: React.ComponentType<React.HTMLAttributes<HTMLElement>>;
  ListboxProps?: { [key: string]: any };
  renderType?: "renderExtra" | "renderSimple" | "renderDefault";
  defaultValue?: { value?: string | number; label?: string };
  variant?: "outlined" | "filled" | "standard";
  fillColor?: "grey" | "green" | "white";
  InputProps?: {};
  valueWhenSelected?: boolean;
}

const OutlinedAutocomplete = React.forwardRef<any, IOutlinedAutocomplete>(
  (props, ref) => {
    const {
      name,
      validate,
      label,
      options,
      disabled,
      fullWidth = true,
      helperText: userHelperText,
      required,
      endAdornment,
      onKeyDown,
      onFocus,
      inputValue,
      id,
      loading,
      ListboxComponent,
      ListboxProps,
      renderType,
      defaultValue,
      variant,
      fillColor,
      InputProps,
      valueWhenSelected,
      ...rest
    } = props;

    const { classes, cx } = useOutlinedAutocompleteStyles();
    const [translate] = useTranslation();
    const { input, meta, ...restTextField } = useField(name, { validate });
    const isError = showError(meta);
    const helperText = isError ? meta.error : userHelperText;
    const theme = useTheme();
    const xsScreen = useMediaQuery(theme.breakpoints.between(0, 300));
    const labelLength = xsScreen ? 40 : 60;

    const renderOptionDefault = (
      props: React.HTMLAttributes<HTMLLIElement>,
      option: {
        label?: string;
        value?: string | number;
      }
    ) => (
      <li {...props} key={option.value}>
        <div className={classes.renderOpDiv}>
          <span style={{ fontWeight: "bold" }}>{option.value || ""}</span>
          {option.label ? (option.value ? ": " : "") + option.label : ""}
        </div>
      </li>
    );
    const renderOptionSimple = (
      props: React.HTMLAttributes<HTMLLIElement>,
      option: {
        label?: string;
        value?: string | number;
      }
    ) => (
      <li {...props} key={option.value}>
        {option.value}
      </li>
    );

    const renderOptionExtra = (
      props: React.HTMLAttributes<HTMLLIElement>,
      option: {
        label?: string;
        value?: string | number;
      }
    ) => {
      let value = "" + option.value;
      let label = option.label ? option.label.trim() : value;
      const isColon = label ? label.indexOf(":") : -1;
      const lblLen = label.length;

      if (isColon > -1) {
        value = label ? label.substring(0, isColon) : "";
        label = label
          ? label.substring(isColon + 1, labelLength) +
            (lblLen > labelLength ? "..." : "")
          : "";
      } else {
        value = "";
        label =
          label.substring(0, labelLength) + (lblLen > labelLength ? "..." : "");
      }

      return (
        <li {...props} key={option.value}>
          <div className={classes.renderOpDiv}>
            <span style={{ fontWeight: "bold" }}>{value}</span>
            {(value ? ": " : "") + label}
          </div>
        </li>
      );
    };

    const renderOption = (
      props: React.HTMLAttributes<HTMLLIElement>,
      option: {
        label?: string;
        value?: string | number;
      }
    ) => {
      switch (renderType) {
        case "renderExtra":
          return renderOptionExtra(props, option);
        case "renderSimple":
          return renderOptionSimple(props, option);
        default:
          return renderOptionDefault(props, option);
      }
    };

    return (
      <Autocomplete
        disabled={meta.submitting || disabled}
        options={options}
        loading={loading}
        loadingText={translate("loading")}
        id={id}
        slotProps={{
          popper: {
            sx: {
              borderTop: "none",
            },
          },
        }}
        fullWidth={fullWidth}
        defaultValue={defaultValue}
        renderOption={renderOption}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        filterOptions={filterOptions}
        getOptionLabel={(option: any) =>
          option && valueWhenSelected ? option.value : option.label
        }
        onChange={(e: {}, value: any) =>
          input.onChange(value && value.value ? value.value : "")
        }
        inputValue={
          endAdornment || inputValue
            ? typeof input.value !== "string"
              ? JSON.stringify(input.value)
              : input.value
            : undefined
        }
        popupIcon={<ExpandMoreOutlined />}
        onBlur={input.onBlur}
        noOptionsText={translate("noOptions", "Nėra tokio pasirinkimo")}
        disableClearable={!!endAdornment}
        ListboxComponent={ListboxComponent}
        ListboxProps={ListboxProps}
        size="small"
        renderInput={(params) => (
          <TextField
            {...params}
            {...input}
            {...restTextField}
            label={label}
            disabled={meta.submitting || disabled}
            error={isError}
            helperText={helperText}
            required={required}
            onKeyDown={onKeyDown}
            onFocus={onFocus}
            inputRef={ref}
            variant={variant}
            slotProps={{
              input: {
                ...params.InputProps,
                ...InputProps,
              },
            }}
            classes={{
              root: cx({
                [classes.whiteFilled]: fillColor === "white",
              }),
            }}
          />
        )}
        classes={{
          root: classes.root,
        }}
        {...rest}
      />
    );
  }
);

export default React.memo(OutlinedAutocomplete);
